<template>
  <v-list 
    :class="{
      'ui-menu-content': true,
      'ui-menu-content--submenu': props.submenu,
      'ui-menu-content--condenced': pMenuProps.condenced
    }"
    :style="{
      'min-width': pMenuProps.menuWidth ? `${pMenuProps.menuWidth}px` : pMenuProps.menuWidth
    }"
  >
    <v-list-item v-if="props.filter" class="ui-menu-content__list-item ui-menu-content__list-item--filter">
      <div class="ui-menu-content__list-item-container">
        <ui-input
          v-model="filterValue"
          class="ui-menu-content__filter"
          :hide-details="1"
        ></ui-input>
      </div>
    </v-list-item>

    <div 
      class="ui-menu-content__content"
      :style="{
        'max-height': pMenuProps.maxHeight ? pMenuProps.maxHeight : '400px'
      }"
    >
      <v-list-item
        v-if="props.filter && itemsFiltered.length === 0"
        class="ui-menu-content__list-item"
      >
        <div class="ui-menu-content__list-item-container">
          <ui-icon :color="$colors.gray40" icon="fa fa-search-minus"></ui-icon>
          <span class="ui-menu-content__list-item-text ml-2">{{t('Not found')}}</span>
        </div>
      </v-list-item>
      
      <slot>
        <ui-menu-content-item
          v-for="(item, index) of itemsFiltered"
          :key="item.key || index"
          :item="item"
          :selected="selectedMenuItem"
          :menu-props="menuProps"
          @action="onAction"
        >
          <template v-if="slots.item" #item="{ item: slotItem }">
            <slot name="item" :item="slotItem"></slot>
          </template>
        </ui-menu-content-item>
      </slot>
    </div>
  </v-list>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from 'vue';
import { useColors, useI18n } from '@/composables';

const slots = useSlots();
const { t } = useI18n();
const $colors = useColors();
const props = withDefaults(
  defineProps<{
    items?: Bemob.ListItem[];
    submenu?: boolean;
    filter?: boolean;
    menuProps?: any;
    selected?: string;
  }>(),
  {
    items: () => [] as Bemob.ListItem[],
    submenu: false,
    filter: false,
    menuProps: () => ({}),
    selected: '',
  },
);

const emit = defineEmits<{
  (e: 'action', item: Bemob.ListItem): void;
}>();

const filterValue = ref('');

const preparedItems = computed(() =>
  props.items.map(item => {
    const title = item.title ? item.title.toLowerCase() : '';
    const subtitle = item.subtitle ? item.subtitle.toLowerCase() : '';
    return `${title} ${subtitle}`;
  }),
);

const selectedMenuItem = computed(() => props.selected || props.menuProps?.selected);

const itemsFiltered = computed(() => {
  if (props.filter && filterValue.value) {
    const filterValueLocal = filterValue.value.toLowerCase();
    return props.items.filter((item, index) =>
      preparedItems.value[index]?.includes(filterValueLocal),
    );
  }
  return props.items;
});

const pMenuProps = computed(() => ({
  menuWidth: 0,
  ...(props.menuProps || {}),
}));

const clear = () => {
  filterValue.value = '';
};

const onAction = (item: Bemob.ListItem) => {
  emit('action', item);
};

defineExpose({ clear });
</script>

<style lang="scss">
.ui-menu-content.v-list {
  border-radius: 8px;
  padding: 0;

  .ui-chevron__icon {
    font-size: 13px;
  }
}

.ui-menu-content__content {
  padding: 4px 0 4px;
  overflow: auto;
  display: flex;
  flex-direction: column;

  > * + * {
    border-top: 1px solid var(--color-gray10);
  }
}

.ui-menu-content--submenu.v-list {
  border-radius: 0 0 8px 8px;
}

.ui-menu-content__list-item-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-grow: 1;
  overflow: hidden;
}

.ui-menu-content__list-item {
  width: 100%;
  font-size: 13px;
  overflow: hidden;

  &.v-list-item {
    min-height: 48px !important;
    padding: 0 16px !important;
    padding-inline: 16px !important;
  }

  .v-list-item__content {
    padding: 0;
    overflow: visible;
  }
}

.ui-menu-content__list-item-text {
  color: var(--color-gray40);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ui-menu-content__list-item--filter {
  overflow: visible;

  .ui-menu-content__list-item-container {
    overflow: visible;
    align-items: center;
  }
}

.ui-menu-content__filter {
  display: flex;
  flex-grow: 1;
  width: 100%;
  height: 32px !important;

  &.ui-input--controls-hide-details {
    height: 32px;
  }

  .v-field {
    --v-field-padding-start: 0;
    --v-field-padding-end: 0;
    --v-input-control-height: 32px;
    box-sizing: border-box;
    min-height: 32px;
    height: 32px;
    overflow: visible;
    border: solid 1px var(--color-gray20) !important;
  }

  .v-field__input,
  input {
    font-size: 12px !important;
    line-height: 30px;
    min-height: 30px !important;
    height: 30px !important;
    max-height: 30px;
    padding: 0 4px 0 8px !important;
    color: rgba(0, 0, 0, 0.87) !important;
  }
}

.ui-menu-content.ui-menu-content--condenced .v-list-item {
  min-height: 28px;
}
</style>
